import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  FileText,
  Palette,
  TrendingUp,
  Globe,
  Smartphone,
  Monitor,
  ShoppingCart,
  CheckSquare,
  Square,
  Zap,
  Loader2,
  X
} from 'lucide-react';
import { projectTypeAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const ProjectTypesManagement = () => {
  const { notify } = useNotificationHook();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProjectType, setSelectedProjectType] = useState(null);
  
  // Bulk actions state
  const [selectedProjectTypes, setSelectedProjectTypes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Fetch project types on component mount
  useEffect(() => {
    fetchProjectTypes();
  }, []);

  const fetchProjectTypes = async () => {
    try {
      setFetchLoading(true);
      setError(null);
      
      const response = await projectTypeAPI.getAllAdmin();
      
      if (response?.data?.success) {
        setProjectTypes(response.data.data.projectTypes || []);
      } else {
        console.error('Failed to fetch project types:', response);
        setError('Failed to load project types');
      }
    } catch (err) {
      console.error('Error fetching project types:', err);
      setError(`Error loading project types: ${err.message}`);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilterActive(e.target.value);
  };

  const handleAddProjectType = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await projectTypeAPI.create(formData);
      
      if (response?.data?.success) {
        await fetchProjectTypes();
        setShowAddModal(false);
      } else {
        throw new Error(response?.data?.message || 'Failed to create project type');
      }
    } catch (err) {
      console.error('Error creating project type:', err);
      setError(`Error creating project type: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProjectType = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await projectTypeAPI.update(selectedProjectType.id, formData);
      
      if (response?.data?.success) {
        await fetchProjectTypes();
        setShowEditModal(false);
        setSelectedProjectType(null);
      } else {
        throw new Error(response?.data?.message || 'Failed to update project type');
      }
    } catch (err) {
      console.error('Error updating project type:', err);
      setError(`Error updating project type: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProjectType = async (projectTypeId) => {
    if (window.confirm('Are you sure you want to delete this project type?')) {
      try {
        setLoading(true);
        setError(null);
        
        const response = await projectTypeAPI.delete(projectTypeId);
        
        if (response?.data?.success) {
          await fetchProjectTypes();
        } else {
          throw new Error(response?.data?.message || 'Failed to delete project type');
        }
      } catch (err) {
        console.error('Error deleting project type:', err);
        setError(`Error deleting project type: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (projectType) => {
    setSelectedProjectType(projectType);
    setShowEditModal(true);
  };

  const getIconComponent = (iconName) => {
    const iconMap = {
      'globe': Globe,
      'smartphone': Smartphone,
      'monitor': Monitor,
      'shopping-cart': ShoppingCart,
      'trending-up': TrendingUp,
      'palette': Palette,
      'file-text': FileText
    };
    return iconMap[iconName] || FileText;
  };

    const getStatusBadge = (isActive) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    return isActive
      ? `${baseClasses} text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/20`
      : `${baseClasses} text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20`;
  };

  // Bulk action functions
  const handleSelectProjectType = (projectTypeId) => {
    setSelectedProjectTypes(prev => 
      prev.includes(projectTypeId) 
        ? prev.filter(id => id !== projectTypeId)
        : [...prev, projectTypeId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProjectTypes([]);
      setSelectAll(false);
    } else {
      setSelectedProjectTypes(projectTypes.map(projectType => projectType.id));
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (is_active) => {
    if (selectedProjectTypes.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await projectTypeAPI.bulkUpdateStatus(selectedProjectTypes, is_active);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedProjectTypes.length} project type(s) status`);
        setSelectedProjectTypes([]);
        setSelectAll(false);
        await fetchProjectTypes();
      } else {
        notify.error('Failed to update project types: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating project types:', error);
      notify.error('Error updating project types: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProjectTypes.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedProjectTypes.length} project type(s)?`)) {
      return;
    }
    
    try {
      setBulkActionLoading(true);
      const response = await projectTypeAPI.bulkDelete(selectedProjectTypes);
      
      if (response.data.success) {
        notify.success(`Successfully deleted ${selectedProjectTypes.length} project type(s)`);
        setSelectedProjectTypes([]);
        setSelectAll(false);
        await fetchProjectTypes();
      } else {
        notify.error('Failed to delete project types: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting project types:', error);
      notify.error('Error deleting project types: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Filter project types based on search and filters
  const filteredProjectTypes = projectTypes.filter(projectType => {
    const matchesSearch = projectType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (projectType.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = filterActive === 'all' || 
                         (filterActive === 'active' && projectType.is_active) ||
                         (filterActive === 'inactive' && !projectType.is_active);
    return matchesSearch && matchesActive;
  });

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              Project Types Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage project categories and types
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Project Type
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:p-6 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Types</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{projectTypes.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {projectTypes.filter(pt => pt.is_active).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inactive</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {projectTypes.filter(pt => !pt.is_active).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search project types..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <select
              value={filterActive}
              onChange={handleFilterChange}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedProjectTypes.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedProjectTypes.length} project type(s) selected
              </span>
              <button
                onClick={() => {
                  setSelectedProjectTypes([]);
                  setSelectAll(false);
                }}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkStatusUpdate(true)}
                disabled={bulkActionLoading}
                className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
              >
                {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckSquare className="h-3 w-3 mr-1" />}
                Activate
              </button>
              <button
                onClick={() => handleBulkStatusUpdate(false)}
                disabled={bulkActionLoading}
                className="px-3 py-1 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded-md flex items-center disabled:opacity-50"
              >
                {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Square className="h-3 w-3 mr-1" />}
                Deactivate
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
                className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
              >
                {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Types Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Project Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Sort Order
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {fetchLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-red-600 dark:text-red-400">
                    {error}
                  </td>
                </tr>
              ) : filteredProjectTypes.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No project types found
                  </td>
                </tr>
              ) : (
                filteredProjectTypes.map((projectType) => {
                  const IconComponent = getIconComponent(projectType.icon);
                  return (
                    <tr key={projectType.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedProjectTypes.includes(projectType.id)}
                          onChange={() => handleSelectProjectType(projectType.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div 
                            className="p-2 rounded-lg mr-3"
                            style={{ backgroundColor: `${projectType.color}20` }}
                          >
                            <IconComponent 
                              className="h-4 w-4" 
                              style={{ color: projectType.color }}
                            />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {projectType.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {projectType.icon || 'No icon'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                          {projectType.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(projectType.is_active)}>
                          {projectType.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {projectType.sort_order}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(projectType)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteProjectType(projectType.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && createPortal(
        <ProjectTypeModal
          isOpen={showAddModal || showEditModal}
          onClose={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setSelectedProjectType(null);
          }}
          onSubmit={showAddModal ? handleAddProjectType : handleEditProjectType}
          title={showAddModal ? 'Add Project Type' : 'Edit Project Type'}
          projectType={selectedProjectType}
          loading={loading}
        />
      , document.body)}
    </div>
  );
};

const ProjectTypeModal = ({ isOpen, onClose, onSubmit, title, projectType = null, loading = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    color: '#3B82F6',
    sort_order: 0,
    is_active: true
  });

  // Update form data when projectType prop changes (for editing)
  useEffect(() => {
    if (projectType) {
      setFormData({
        name: projectType.name || '',
        description: projectType.description || '',
        icon: projectType.icon || '',
        color: projectType.color || '#3B82F6',
        sort_order: projectType.sort_order || 0,
        is_active: projectType.is_active !== undefined ? projectType.is_active : true
      });
    } else {
      // Reset form for new project type
      setFormData({
        name: '',
        description: '',
        icon: '',
        color: '#3B82F6',
        sort_order: 0,
        is_active: true
      });
    }
  }, [projectType]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData({
      ...formData,
      [name]: newValue
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Icon
              </label>
              <select
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select Icon</option>
                <option value="globe">Globe</option>
                <option value="smartphone">Smartphone</option>
                <option value="monitor">Monitor</option>
                <option value="shopping-cart">Shopping Cart</option>
                <option value="trending-up">Trending Up</option>
                <option value="palette">Palette</option>
                <option value="file-text">File Text</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Color
              </label>
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                name="sort_order"
                value={formData.sort_order}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  );
};

export default ProjectTypesManagement; 